import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Appointment } from '@emr/shared';

import { getNoShowCancellationSummary } from '../api/noShowSummary.ts';
import { Button, ErrorText, Field, Panel } from '../components/primitives.tsx';
import { useDateTimeFormat } from '../format/dateTimeFormatContext.ts';
import { formatDate } from '../format/formatDateTime.ts';
import { addDays } from './weekRange.ts';
import { usePatient } from './usePatient.ts';

/** How many trailing days the panel defaults to when it first mounts. Arbitrary but reasonable for a
 * "how are we doing lately" glance — a longer window is one date-field edit away. */
const DEFAULT_WINDOW_DAYS = 30;

function localDateInputValue(date: Date): string {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** A `date` input's own value (`YYYY-MM-DD`, local) to the start-of-that-day UTC instant the summary
 * query needs — matching `weekRangeToQuery`'s own `Z`-suffixed convention. `date` inputs have no
 * timezone of their own, so this reads the value as local midnight, the same way a person typing
 * "March 1" means their own local March 1st. */
function dateInputToInstant(value: string): string | null {
  if (value === '') return null;
  const [year, month, day] = value.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) return null;
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function formatPercent(rate: number): string {
  return `${Math.round(rate * 100).toString()}%`;
}

function RecentAppointmentRow({ appointment }: { readonly appointment: Appointment }) {
  const { preference } = useDateTimeFormat();
  const patient = usePatient(appointment.patientId);
  const { t } = useTranslation('schedule');
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-2 border-b border-row-line py-1.5 last:border-b-0">
      <span className="text-ink">
        {patient.data === undefined
          ? patient.isError
            ? t('list.unknownPatient')
            : t('list.loading')
          : `${patient.data.lastName}, ${patient.data.firstName}`}
      </span>
      <span className="text-sm text-ink-muted">{formatDate(appointment.startsAt, preference)}</span>
    </li>
  );
}

/**
 * No-show/cancellation reporting (`BACKLOG.md` K3): structured visibility on top of the status data
 * `K1`/`K2` already record — a rate a practice can actually review, plus the actual recent
 * cancelled/no-show appointments, not just a per-appointment status flag nobody aggregates.
 */
export function NoShowSummaryPanel() {
  const { t } = useTranslation('schedule');
  const [range, setRange] = useState(() => {
    const to = new Date();
    return { from: addDays(to, -DEFAULT_WINDOW_DAYS), to };
  });
  const [fromInput, setFromInput] = useState(() => localDateInputValue(range.from));
  const [toInput, setToInput] = useState(() => localDateInputValue(range.to));

  const query = useQuery({
    queryKey: ['no-show-summary', range.from.toISOString(), range.to.toISOString()],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      getNoShowCancellationSummary(
        { from: range.from.toISOString(), to: range.to.toISOString() },
        signal,
      ),
  });

  function applyRange() {
    const from = dateInputToInstant(fromInput);
    const toDay = dateInputToInstant(toInput);
    if (from === null || toDay === null) return;
    // The `to` bound is half-open (`listAppointmentsQuerySchema`'s own `[from, to)` convention), so
    // a range typed as "through March 31" needs `to` at the *start of April 1*, not March 31's own
    // midnight — otherwise every appointment on the last day typed would be silently excluded.
    setRange({ from: new Date(from), to: addDays(new Date(toDay), 1) });
  }

  return (
    <Panel title={t('noShowSummary.panelTitle')}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field
            type="date"
            label={t('noShowSummary.fromLabel')}
            value={fromInput}
            onChange={(event) => {
              setFromInput(event.target.value);
            }}
          />
          <Field
            type="date"
            label={t('noShowSummary.toLabel')}
            value={toInput}
            onChange={(event) => {
              setToInput(event.target.value);
            }}
          />
          <Button variant="secondary" onClick={applyRange}>
            {t('noShowSummary.apply')}
          </Button>
        </div>

        {query.data === undefined ? (
          query.isError ? (
            <ErrorText message={query.error instanceof Error ? query.error.message : 'Error'} />
          ) : (
            <p className="m-0 text-sm text-ink-muted">{t('noShowSummary.loading')}</p>
          )
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-control border border-line p-3">
                <p className="m-0 text-2xl font-semibold text-ink">
                  {query.data.totalAppointments}
                </p>
                <p className="m-0 text-sm text-ink-muted">{t('noShowSummary.total')}</p>
              </div>
              <div className="rounded-control border border-line p-3">
                <p className="m-0 text-2xl font-semibold text-warning">
                  {query.data.noShowRate === null ? '—' : formatPercent(query.data.noShowRate)}
                </p>
                <p className="m-0 text-sm text-ink-muted">
                  {t('noShowSummary.noShowRate', { count: query.data.noShowCount })}
                </p>
              </div>
              <div className="rounded-control border border-line p-3">
                <p className="m-0 text-2xl font-semibold text-danger">
                  {query.data.cancellationRate === null
                    ? '—'
                    : formatPercent(query.data.cancellationRate)}
                </p>
                <p className="m-0 text-sm text-ink-muted">
                  {t('noShowSummary.cancellationRate', { count: query.data.cancelledCount })}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <h3 className="mt-0 mb-2 text-sm font-semibold text-ink">
                  {t('noShowSummary.recentNoShows')}
                </h3>
                {query.data.recentNoShows.length === 0 ? (
                  <p className="m-0 text-sm text-ink-muted">{t('noShowSummary.none')}</p>
                ) : (
                  <ul className="m-0 flex list-none flex-col p-0">
                    {query.data.recentNoShows.map((appointment) => (
                      <RecentAppointmentRow key={appointment.id} appointment={appointment} />
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h3 className="mt-0 mb-2 text-sm font-semibold text-ink">
                  {t('noShowSummary.recentCancellations')}
                </h3>
                {query.data.recentCancellations.length === 0 ? (
                  <p className="m-0 text-sm text-ink-muted">{t('noShowSummary.none')}</p>
                ) : (
                  <ul className="m-0 flex list-none flex-col p-0">
                    {query.data.recentCancellations.map((appointment) => (
                      <RecentAppointmentRow key={appointment.id} appointment={appointment} />
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}
